/**
 * Food that has passed its date, or is about to.
 *
 * WHY THIS IS ON THE STOCK SCREEN AND NOT ITS OWN
 * -----------------------------------------------
 * It is the same question as "what have we got" asked one day later, and the
 * person who reads one reads the other. Putting it behind another click makes
 * it something somebody remembers to check rather than something they see.
 *
 * WHAT THE FIGURES ARE
 * --------------------
 * Deliveries, not what is left of them. Stock is held as one quantity per
 * ingredient with no batch followed through consumption, so nothing here
 * knows which of the twenty kilos came in on Tuesday. That is said on the
 * panel rather than left for somebody to assume otherwise, because a manager
 * reading a quantity will take it for what is on the shelf.
 *
 * WHY AN EMPTY PANEL STILL SAYS SOMETHING
 * ---------------------------------------
 * "Nothing is expiring" and "no delivery has ever had a date entered" look
 * identical, and here the second one is true. An empty panel that stays quiet
 * would be read as reassurance.
 */
import { useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../../lib/api.js';
import { DataTable } from '../../components/ui/DataTable.jsx';

/** How near is near enough to act on. */
const WINDOWS = [7, 14, 30];

export function ExpiryPanel({ withinDays = 7, onWindowChange }) {
  const expiring = useQuery({
    queryKey: ['inventory', 'expiring', withinDays],
    queryFn: () => api.get(withQuery('/inventory/expiring', { within_days: withinDays })),
  });

  /**
   * Guarded the whole way down, not just at the top.
   *
   * `data?.expired.lines` only checks that an answer arrived — if it arrived
   * without an `expired` key, reading `.lines` off undefined takes the whole
   * stock screen down with it. A panel that cannot show its own figures
   * should show nothing, not remove the page around it.
   */
  const data = expiring.data;
  const expired = data?.expired?.lines ?? [];
  const soon = data?.expiringSoon?.lines ?? [];

  return (
    <section className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-medium text-ink">Dates</h2>
          <p className="mt-0.5 text-sm text-ink-3">
            What has gone past its date, and what is close.
          </p>
        </div>

        <div className="flex gap-1">
          {WINDOWS.map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => onWindowChange?.(days)}
              className={`rounded-lg border px-3 py-2 text-sm ${
                days === withinDays
                  ? 'border-brand-400 text-ink'
                  : 'border-rule text-ink-2 hover:text-ink'
              }`}
            >
              {days} days
            </button>
          ))}
        </div>
      </div>

      {/*
        Loud, because this is food. Everything else on this screen is a
        question about money.
      */}
      {expired.length > 0 && (
        <p className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {expired.length} {expired.length === 1 ? 'delivery has' : 'deliveries have'} passed their
          date. Anything left of {expired.length === 1 ? 'it' : 'them'} should come off the shelf
          before service.
        </p>
      )}

      {data?.coverage?.warning && (
        <p className="mb-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
          {data.coverage.warning}
        </p>
      )}

      <DataTable
        rows={[...expired, ...soon]}
        loading={expiring.isLoading}
        error={expiring.isError}
        searchable
        searchKeys={['ingredient', 'supplier', 'batchNumber']}
        columns={[
          {
            key: 'ingredient',
            label: 'Ingredient',
            tone: (row) => (row.daysLeft < 0 ? 'text-bad' : 'text-ink'),
          },
          {
            key: 'quantity',
            label: 'Delivered',
            align: 'right',
            render: (row) => `${row.quantity} ${row.unit ?? ''}`.trim(),
          },
          { key: 'expiryDate', label: 'Date' },
          {
            key: 'daysLeft',
            label: 'Left',
            align: 'right',
            // "3 days ago" reads faster than a negative number, and this is a
            // screen somebody scans rather than studies.
            render: (row) =>
              row.daysLeft < 0
                ? `${Math.abs(row.daysLeft)} ${Math.abs(row.daysLeft) === 1 ? 'day' : 'days'} ago`
                : row.daysLeft === 0
                  ? 'Today'
                  : `${row.daysLeft} ${row.daysLeft === 1 ? 'day' : 'days'}`,
            tone: (row) =>
              row.daysLeft < 0 ? 'text-bad' : row.daysLeft <= 2 ? 'text-warn' : 'text-ink-2',
          },
          { key: 'receivedDate', label: 'Came in' },
          { key: 'supplier', label: 'From' },
        ]}
      />

      {/* Said on the panel, because a manager reading a quantity will
          otherwise take it for what is on the shelf. */}
      {data?.coverage?.note && <p className="mt-2 text-xs text-ink-3">{data.coverage.note}</p>}
    </section>
  );
}
